import React from "react";
import { AssociatedPerson } from "../../../../types/cdd-records.types";
import { MemberCard, getPositionColor } from "./AssociationMemberCard";

interface CommitteePosition {
  position: string;
  personId: string;
  appointmentDate?: string;
}

interface AssociationStructureMapProps {
  associationName: string;
  members: AssociatedPerson[];
  positions: CommitteePosition[];
  onPersonClick?: (person: AssociatedPerson) => void;
}

export const AssociationStructureMap: React.FC<
  AssociationStructureMapProps
> = ({ associationName, members, positions, onPersonClick }) => {
  // Group members by position type
  const officeBearers = positions.filter((p) =>
    ["president", "vice president", "secretary", "treasurer", "chair"].includes(
      p.position.toLowerCase(),
    ),
  );

  // Get office bearer members
  const officeBearerMembers = officeBearers
    .map((p) => members.find((m) => m.id === p.personId))
    .filter((m): m is AssociatedPerson => m !== undefined);

  // Get ordinary members (those without office bearer positions)
  const officeBearerIds = new Set(officeBearers.map((p) => p.personId));
  const ordinaryMembers = members.filter((m) => !officeBearerIds.has(m.id));

  // Function to get positions for a member
  const getMemberPositions = (memberId: string): string[] => {
    return positions
      .filter((p) => p.personId === memberId)
      .map((p) => p.position);
  };

  return (
    <div className="space-y-6">
      {/* Visual Structure Diagram */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            Association Committee Structure
          </h3>
        </div>

        <div className="p-6">
          <div className="flex flex-col items-center gap-4">
            {/* Association Box at top */}
            <div className="px-10 py-5 bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl text-white shadow-lg">
              <div className="flex items-center gap-3">
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                <div>
                  <div className="text-xs text-teal-200 uppercase">
                    Association
                  </div>
                  <div className="text-lg font-bold">{associationName}</div>
                </div>
              </div>
            </div>

            {/* Arrow down */}
            <svg
              width="24"
              height="32"
              viewBox="0 0 24 32"
              className="text-gray-400"
            >
              <line
                x1="12"
                y1="0"
                x2="12"
                y2="24"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="4 2"
              />
              <polygon points="6,24 12,32 18,24" fill="currentColor" />
            </svg>

            {/* Committee Box */}
            <div className="w-full max-w-3xl">
              <div className="px-6 py-3 bg-slate-100 rounded-t-lg border border-slate-200 border-b-0">
                <div className="text-center text-sm font-semibold text-slate-700 uppercase">
                  Management Committee
                </div>
              </div>

              {/* Office Bearers Row */}
              {officeBearers.length > 0 && (
                <div className="px-6 py-4 bg-white border-x border-slate-200">
                  <div className="text-xs text-gray-500 font-semibold uppercase mb-3 text-center">
                    Office Bearers
                  </div>
                  <div className="flex flex-wrap justify-center gap-3">
                    {officeBearers.map((position, idx) => {
                      const member = members.find(
                        (m) => m.id === position.personId,
                      );
                      const colors = getPositionColor(position.position);
                      return (
                        <div
                          key={idx}
                          onClick={() => member && onPersonClick?.(member)}
                          className={`px-4 py-2 rounded-lg border-2 ${colors.border} ${colors.bg} cursor-pointer hover:shadow-md transition-all`}
                        >
                          <div
                            className={`text-xs font-semibold uppercase ${colors.text}`}
                          >
                            {position.position}
                          </div>
                          <div className="text-sm font-medium text-gray-900 mt-1">
                            {member
                              ? `${member.firstName} ${member.lastName}`
                              : "Unknown"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Divider */}
              {officeBearers.length > 0 && ordinaryMembers.length > 0 && (
                <div className="border-x border-slate-200 bg-white">
                  <div className="mx-6 border-t border-dashed border-gray-300"></div>
                </div>
              )}

              {/* Ordinary Committee Members */}
              {ordinaryMembers.length > 0 && (
                <div className="px-6 py-4 bg-white border-x border-b border-slate-200 rounded-b-lg">
                  <div className="text-xs text-gray-500 font-semibold uppercase mb-3 text-center">
                    Committee Members ({ordinaryMembers.length})
                  </div>
                  <div className="flex flex-wrap justify-center gap-2">
                    {ordinaryMembers.slice(0, 6).map((member) => {
                      const memberPositions = getMemberPositions(member.id);
                      return (
                        <span
                          key={member.id}
                          onClick={() => onPersonClick?.(member)}
                          className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm cursor-pointer hover:bg-gray-200 transition-colors flex items-center gap-2"
                        >
                          <span className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium">
                            {member.firstName[0]}
                            {member.lastName[0]}
                          </span>
                          {member.firstName} {member.lastName}
                          {memberPositions.length > 0 && (
                            <span className="text-xs text-gray-500">
                              ({memberPositions[0]})
                            </span>
                          )}
                        </span>
                      );
                    })}
                    {ordinaryMembers.length > 6 && (
                      <span className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full text-sm">
                        +{ordinaryMembers.length - 6} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {officeBearers.length === 0 && ordinaryMembers.length === 0 && (
                <div className="px-6 py-8 bg-white border-x border-b border-slate-200 rounded-b-lg text-center">
                  <p className="text-gray-500">
                    No committee members on record
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Member Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Office Bearers */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-teal-50">
            <div className="flex items-center justify-between">
              <h4 className="text-md font-semibold text-teal-900 flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
                Office Bearers
              </h4>
              <span className="px-2 py-1 bg-teal-100 text-teal-700 text-sm font-medium rounded">
                {officeBearerMembers.length}
              </span>
            </div>
            <p className="text-xs text-teal-700 mt-1">
              Key positions responsible for governance
            </p>
          </div>
          <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
            {officeBearerMembers.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                positions={getMemberPositions(member.id)}
                onClick={onPersonClick}
              />
            ))}
            {officeBearerMembers.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                No office bearers on record
              </p>
            )}
          </div>
        </div>

        {/* All Committee Members */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h4 className="text-md font-semibold text-gray-900 flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                All Committee Members
              </h4>
              <span className="px-2 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded">
                {members.length}
              </span>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Complete list of committee members
            </p>
          </div>
          <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
            {members.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                positions={getMemberPositions(member.id)}
                onClick={onPersonClick}
              />
            ))}
            {members.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                No committee members on record
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssociationStructureMap;
